import { ArrowLeft } from "lucide-react";
import { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../ui/v2/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/v2/tooltip";

import { cn } from "@/lib/utils/cn";

interface ModernPageLayoutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  children: ReactNode;
  breadcrumbs?: ReactNode;
  title?: ReactNode
  subtitle?: ReactNode;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  backbutton?: {
    label: string;
    href?: string;
  };
}

function ModernPageLayout({
  children,
  breadcrumbs,
  title,
  subtitle,
  badge,
  actions,
  backbutton,
  className,
}: ModernPageLayoutProps) {
  const navigate = useNavigate();

  const backArrow = (
    <ArrowLeft
      className={cn(
        "h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors",
        "md:h-5 md:w-5"
      )}
    />
  );

  return (
    <div className={cn("flex flex-col gap-4", "md:gap-6", className)}>
      {breadcrumbs && <div className="breadcrumb-section">{breadcrumbs}</div>}

      {(title || subtitle || badge || actions) && (
        <div
          className={cn(
            "flex w-full flex-col justify-between gap-0",
            "md:gap-4 md:flex-row md:items-center"
          )}
        >
          <div
            className={cn("flex items-center gap-4 mb-6", "md:gap-2 md:mb-2")}
          >
            {backbutton && (
              <div className={cn("relative")}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {backbutton.href ? (
                      <Button asChild variant={"ghost"} size={"icon"}>
                        <Link to={backbutton.href}>{backArrow}</Link>
                      </Button>
                    ) : (
                      <Button variant={"ghost"} size={"icon"} onClick={() => navigate(-1)}>
                        {backArrow}
                      </Button>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>{backbutton.label}</TooltipContent>
                </Tooltip>
              </div>
            )}
            <div className="min-w-0 flex-1">
              {(title || badge) && (
                <div
                  className={cn(
                    "flex flex-col gap-2",
                    "md:flex-row md:items-center md:gap-4"
                  )}
                >
                  {title && (
                    <h1
                      className={cn(
                        "text-xl font-bold text-foreground mb-1 tracking-tight text-wrap min-w-0",
                        "md:text-2xl",
                        "lg:text-3xl"
                      )}
                    >
                      {title}
                    </h1>
                  )}
                  {badge}
                </div>
              )}
              {subtitle && (
                <p
                  className={cn(
                    "text-sm text-muted-foreground max-w-2xl text-wrap",
                    "md:text-base"
                  )}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {actions && (
            <div className={cn("flex items-center gap-2 w-full", "md:w-auto")}>
              {actions}
            </div>
          )}
        </div>
      )}

      <div className="content-section flex flex-col gap-4">{children}</div>
    </div>
  );
}

export default ModernPageLayout;
